import { z } from "zod/v4";

export const Amount = z.string().refine((amount) => /^(0|[1-9]\d*)(\.\d+)?$/.test(amount), {
    message: "Invalid amount",
});
export type Amount = z.infer<typeof Amount>;

// EVM
export const EVMAddressRegex = /^(0x|0x[a-fA-F0-9]{40})$/;
export const EVMTokenAddress = z.string().refine((a) => EVMAddressRegex.test(a), {
    message: "Invalid EVM token address",
});
export type EVMTokenAddress = z.infer<typeof EVMTokenAddress>;

export const EVMAddress = EVMTokenAddress.refine((val) => val !== "0x", {
    message: "Invalid EVM address",
});
export type EVMAddress = z.infer<typeof EVMAddress>;

// Solana
export const SolAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
export const SolNativeRegex = /^So[1-2]{41}$/;
export const SolTokenAddress = z.string().refine((a) => SolAddressRegex.test(a) || SolNativeRegex.test(a), {
    message: "Invalid Solana token address",
});
export type SolTokenAddress = z.infer<typeof SolTokenAddress>;
export const SolAddress = z.string().refine((a) => SolAddressRegex.test(a), {
    message: "Invalid Solana address",
});
export type SolAddress = z.infer<typeof SolAddress>;

// Address
export const Address = z.union([EVMAddress, SolAddress]);
export type Address = z.infer<typeof Address>;

// Fiat
export const Fiat = z.string().refine((fiat) => /^[a-zA-Z_]{3,}$/.test(fiat), {
    message: "Invalid fiat",
});
export type Fiat = z.infer<typeof Fiat>;

export const FiatDetails = z.object({
    issuer: z.string(),
    name: z.string(),
    alias: z.string().optional(),
    symbol: z.string(),
    decimals: z.number(),
});
export type FiatDetails = z.infer<typeof FiatDetails>;

// Token
export const Name = z.string();
export const Symbol = z.string().regex(/^[a-zA-Z]\w{1,7}$/, {
    message: "Must be a short alpha-numeric symbol",
});
export const Decimals = z.number().min(0).max(18);

export const ImageUrl = z.string().url();
export const CoinGeckoId = z.string();

export const EVMTokenPointer = z.object({
    chain: z.string(),
    address: EVMTokenAddress,
});

export const SolTokenPointer = z.object({
    chain: z.literal("solana"),
    address: SolTokenAddress,
});

export const FlexibleTokenPointer = z.object({
    chain: z.string(),
    address: z.string(),
});

export const TokenPointer = z.union([EVMTokenPointer, SolTokenPointer, FlexibleTokenPointer]);

export const Token = z.string().refine(
    (token) => {
        const [chain, address] = token.split(":");
        const result = TokenPointer.safeParse({ chain, address });
        return result.success;
    },
    {
        message: "Invalid token",
    }
);
export type Token = z.infer<typeof Token>;

export const TokenInfo = z.object({
    name: Name,
    symbol: Symbol,
    decimals: Decimals,
    alias: Symbol.optional(),
    image_url: ImageUrl.optional(),
    coingecko_id: CoinGeckoId.optional(),
    wrapper: Symbol.optional(),
    is_native: z.boolean().optional(),
});

export const EVMTokenDetails = EVMTokenPointer.extend(TokenInfo.shape);
export type EVMTokenDetails = z.infer<typeof EVMTokenDetails>;

export const SolTokenDetails = SolTokenPointer.extend(TokenInfo.shape);
export type SolTokenDetails = z.infer<typeof SolTokenDetails>;

export const FlexibleTokenDetails = FlexibleTokenPointer.extend(TokenInfo.shape);
export type FlexibleTokenDetails = z.infer<typeof FlexibleTokenDetails>;

export const TokenDetails = z.union([EVMTokenDetails, SolTokenDetails, FlexibleTokenDetails]);
export type TokenDetails = z.infer<typeof TokenDetails>;

// Asset
export const Asset = z.union([Token, Fiat]);
export type Asset = z.infer<typeof Asset>;
export const AssetDetails = z.union([TokenDetails, FiatDetails]);
export type AssetDetails = z.infer<typeof AssetDetails>;

export const Realm = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("chain"),
        chain: z.string(),
    }),
    z.object({
        type: z.literal("issuer"),
        issuer: z.string(),
    }),
]);
export type Realm = z.infer<typeof Realm>;

// Ramp
export const RampName = z.string().transform((ramp_name) => ramp_name.toLowerCase());
export type RampName = z.infer<typeof RampName>;

export const RampType = z.preprocess(
    (ramp_type) => (typeof ramp_type === "string" ? ramp_type.toUpperCase() : ramp_type),
    z.enum(["ONRAMP", "OFFRAMP"])
);
export type RampType = z.infer<typeof RampType>;

// Transaction
// TODO: confirm the fields that accommodate ethers and viem
export const TransactionRequest = z.object({
    to: EVMAddress,
    from: EVMAddress.optional(),
    nonce: z.number().optional(),
    gasLimit: z.bigint().optional(),
    gasPrice: z.bigint().optional(),
    maxPriorityFeePerGas: z.bigint().optional(),
    maxFeePerGas: z.bigint().optional(),
    data: z.string().optional(),
    value: z.bigint().optional(),
    chainId: z.number(),
});
export type TransactionRequest = z.infer<typeof TransactionRequest>;

// TODO: confirm the fields that accommodate ethers and viem
export const TransactionReceipt = z.object({
    transactionHash: z.string().optional(),
    blockHash: z.string().optional(),
    blockNumber: z.number().optional(),
    from: EVMAddress.optional(),
    to: EVMAddress.optional(),
    rawReceipt: z.any(), // Preserves the original receipt from ethers or viem
});
export type TransactionReceipt = z.infer<typeof TransactionReceipt>;

// Chain
export const EVMChainConfig = z.object({
    chain_id: z.bigint(),
    network: z.string(),
    native_currency: z.object({
        name: z.string(),
        symbol: z.string(),
        decimals: z.number(),
    }),
    rpc: z.string(),
    image: z.string(),
    is_testnet: z.boolean(),
    explorer: z.string(),
});
export type EVMChainConfig = z.infer<typeof EVMChainConfig>;

export const SolChainConfig = z.object({
    chain_id: z.bigint(),
    network: z.string(),
});
export type SolChainConfig = z.infer<typeof SolChainConfig>;

export const ChainConfig = z.union([EVMChainConfig, SolChainConfig]);
export type ChainConfig = z.infer<typeof ChainConfig>;

export const OnrampMethod = z.enum([
    "CREDIT_CARD",
    "DEBIT_CARD",
    "ACH",
    "FIAT_BALANCE",
    "TOKEN_BALANCE",
    "APPLE_PAY",
    "PAYPAL",
    "VENMO",
    "REVOLUT",
    "GOOGLE_PAY",
    "SEPA",
    "FASTER_PAYMENTS",
    "PIX",
    "UPI",
    "WIRE",
]);
export type OnrampMethod = z.infer<typeof OnrampMethod>;
